import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useAuth, apiClient } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import DashboardLayout from "@/components/layout/DashboardLayout";
import {
  Users,
  Search,
  Loader2,
  ExternalLink,
  Globe,
  Lock,
  UserPlus,
  TrendingUp,
  MapPin,
  Filter,
  RefreshCw,
  Sparkles,
  Facebook,
  Check,
  Star
} from "lucide-react";

const industryCategories = [
  { id: "marketing", label: "Marketing & Advertising", keywords: ["digital marketing", "social media marketing", "content marketing", "SEO"] },
  { id: "tech", label: "Tech & Startups", keywords: ["startups", "tech entrepreneurs", "SaaS", "developers"] },
  { id: "ecommerce", label: "E-commerce", keywords: ["ecommerce", "dropshipping", "amazon sellers", "shopify"] },
  { id: "health", label: "Health & Wellness", keywords: ["fitness", "wellness", "nutrition", "mental health"] },
  { id: "finance", label: "Finance & Business", keywords: ["entrepreneurs", "small business", "investing", "real estate"] },
  { id: "creative", label: "Creative & Design", keywords: ["graphic design", "content creators", "photography", "video"] },
  { id: "local", label: "Local Business", keywords: ["local business", "small business owners", "community"] },
  { id: "coaching", label: "Coaching & Education", keywords: ["coaches", "online courses", "educators", "training"] }
];

export default function GroupFinderPage() {
  const { user } = useAuth();
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedCategories, setSelectedCategories] = useState([]); // Changed to array for multiple
  const [loading, setLoading] = useState(false);
  const [groups, setGroups] = useState([]);
  const [filters, setFilters] = useState({
    type: "all", // all, public, private
    size: "all", // all, small, medium, large
    location: ""
  });
  const [savedGroups, setSavedGroups] = useState([]);
  const [locationSuggestions, setLocationSuggestions] = useState([]);
  const [showLocationDropdown, setShowLocationDropdown] = useState(false);

  // Canadian Postal Code Database (FSA - Forward Sortation Areas)
  const canadianPostalCodes = {
    // Ontario
    "N5": "London West, Ontario, Canada",
    "N6": "London Central, Ontario, Canada", 
    "N6A": "Downtown London, Ontario, Canada",
    "N6B": "Old East London, Ontario, Canada",
    "N6C": "South London, Ontario, Canada",
    "N6E": "White Oaks, London, Ontario, Canada",
    "N6G": "Byron, London, Ontario, Canada",
    "N6H": "Oakridge, London, Ontario, Canada",
    "N6J": "Westmount, London, Ontario, Canada",
    "N6K": "Hyde Park, London, Ontario, Canada",
    "N6L": "Lambeth, London, Ontario, Canada",
    "N6M": "Masonville, London, Ontario, Canada",
    "N6N": "Summerside, London, Ontario, Canada",
    "N6P": "Lambeth South, London, Ontario, Canada",
    "M5": "Downtown Toronto, Ontario, Canada",
    "M4": "East York, Toronto, Ontario, Canada",
    "M6": "West Toronto, Ontario, Canada",
    "M9": "Etobicoke, Toronto, Ontario, Canada",
    "L5": "Mississauga, Ontario, Canada",
    "L4": "Brampton, Ontario, Canada",
    "K1": "Ottawa Central, Ontario, Canada",
    "K2": "Ottawa West, Ontario, Canada",
    "N2": "Kitchener-Waterloo, Ontario, Canada",
    "L8": "Hamilton, Ontario, Canada",
    "P3": "Sudbury, Ontario, Canada",
    "K9": "Peterborough, Ontario, Canada",
    // Quebec
    "H2": "Montreal Central, Quebec, Canada",
    "H3": "Montreal Downtown, Quebec, Canada",
    "H4": "Montreal West Island, Quebec, Canada",
    "G1": "Quebec City, Quebec, Canada",
    "J4": "Longueuil, Quebec, Canada",
    // British Columbia
    "V5": "Vancouver East, BC, Canada",
    "V6": "Vancouver Downtown, BC, Canada",
    "V7": "North Vancouver, BC, Canada",
    "V3": "Surrey, BC, Canada",
    "V8": "Victoria, BC, Canada",
    // Alberta
    "T2": "Calgary Central, Alberta, Canada",
    "T3": "Calgary NW, Alberta, Canada",
    "T5": "Edmonton Central, Alberta, Canada",
    "T6": "Edmonton South, Alberta, Canada",
    // Manitoba
    "R3": "Winnipeg Central, Manitoba, Canada",
    "R2": "Winnipeg North, Manitoba, Canada",
    // Saskatchewan
    "S7": "Saskatoon, Saskatchewan, Canada",
    "S4": "Regina, Saskatchewan, Canada",
    // Nova Scotia
    "B3": "Halifax, Nova Scotia, Canada",
    // New Brunswick
    "E1": "Moncton, New Brunswick, Canada",
  };

  // Comprehensive worldwide location database - prioritize Canada first
  const allLocations = [
    // Canada - Cities (shown first for Canadian users)
    "London, Ontario, Canada",
    "Toronto, Ontario, Canada", 
    "Mississauga, Ontario, Canada",
    "Brampton, Ontario, Canada",
    "Hamilton, Ontario, Canada",
    "Ottawa, Ontario, Canada",
    "Kitchener, Ontario, Canada",
    "Waterloo, Ontario, Canada",
    "Windsor, Ontario, Canada",
    "Oshawa, Ontario, Canada",
    "Montreal, Quebec, Canada", 
    "Quebec City, Quebec, Canada",
    "Laval, Quebec, Canada",
    "Vancouver, BC, Canada",
    "Surrey, BC, Canada",
    "Burnaby, BC, Canada",
    "Victoria, BC, Canada",
    "Calgary, Alberta, Canada", 
    "Edmonton, Alberta, Canada",
    "Winnipeg, Manitoba, Canada",
    "Saskatoon, Saskatchewan, Canada",
    "Regina, Saskatchewan, Canada",
    "Halifax, Nova Scotia, Canada",
    "St. John's, Newfoundland, Canada",
    
    // USA - Major Cities
    "New York, NY, USA", "Los Angeles, CA, USA", "Chicago, IL, USA", "Houston, TX, USA",
    "Phoenix, AZ, USA", "Philadelphia, PA, USA", "San Antonio, TX, USA", "San Diego, CA, USA",
    "Dallas, TX, USA", "San Jose, CA, USA", "Austin, TX, USA", "Jacksonville, FL, USA",
    "San Francisco, CA, USA", "Seattle, WA, USA", "Denver, CO, USA", "Washington, DC, USA",
    "Boston, MA, USA", "Nashville, TN, USA", "Las Vegas, NV, USA", "Portland, OR, USA",
    "Miami, FL, USA", "Atlanta, GA, USA", "Detroit, MI, USA", "Minneapolis, MN, USA",
    
    // United Kingdom
    "London, United Kingdom", "Manchester, United Kingdom", "Birmingham, United Kingdom",
    "Glasgow, Scotland, UK", "Liverpool, United Kingdom", "Leeds, United Kingdom",
    "Edinburgh, Scotland, UK", "Bristol, United Kingdom",
    
    // Europe
    "Paris, France", "Berlin, Germany", "Munich, Germany", "Madrid, Spain",
    "Barcelona, Spain", "Rome, Italy", "Milan, Italy", "Amsterdam, Netherlands",
    "Brussels, Belgium", "Vienna, Austria", "Zurich, Switzerland",
    "Stockholm, Sweden", "Copenhagen, Denmark", "Dublin, Ireland",
    
    // Asia Pacific
    "Tokyo, Japan", "Seoul, South Korea", "Beijing, China", "Shanghai, China",
    "Hong Kong", "Singapore", "Bangkok, Thailand", "Sydney, Australia",
    "Melbourne, Australia", "Auckland, New Zealand",
    
    // India
    "Mumbai, India", "Delhi, India", "Bangalore, India", "Chennai, India",
    "Hyderabad, India", "Pune, India", "Kolkata, India",
    
    // Middle East
    "Dubai, UAE", "Abu Dhabi, UAE", "Tel Aviv, Israel", "Istanbul, Turkey",
    
    // Latin America
    "Mexico City, Mexico", "São Paulo, Brazil", "Buenos Aires, Argentina",
    "Bogotá, Colombia", "Lima, Peru", "Santiago, Chile"
  ];

  const handleLocationInput = (value) => {
    setFilters({ ...filters, location: value });
    
    if (value.length > 0) {
      let suggestions = [];
      const upperValue = value.toUpperCase();
      
      // Check if input looks like a Canadian postal code (starts with letter + number)
      const isCanadianPostalPattern = /^[A-Za-z]\d/.test(value);
      
      if (isCanadianPostalPattern) {
        // Search Canadian postal codes
        const matchingPostalCodes = Object.entries(canadianPostalCodes)
          .filter(([code, location]) => code.startsWith(upperValue))
          .map(([code, location]) => `${code} - ${location}`);
        
        suggestions = matchingPostalCodes.slice(0, 8);
      }
      
      // Also search city names
      const matchingCities = allLocations.filter(loc => 
        loc.toLowerCase().includes(value.toLowerCase())
      );
      
      // Combine results - postal codes first for Canada, then cities
      if (isCanadianPostalPattern) {
        suggestions = [...suggestions, ...matchingCities.slice(0, 5 - suggestions.length)];
      } else {
        // Prioritize Canadian cities if user types a city name
        const canadianMatches = matchingCities.filter(loc => loc.includes("Canada"));
        const otherMatches = matchingCities.filter(loc => !loc.includes("Canada"));
        suggestions = [...canadianMatches.slice(0, 4), ...otherMatches.slice(0, 4)].slice(0, 8);
      }
      
      setLocationSuggestions(suggestions);
      setShowLocationDropdown(suggestions.length > 0);
    } else {
      setLocationSuggestions([]);
      setShowLocationDropdown(false);
    }
  };

  const selectLocation = (loc) => {
    // Extract just the location name if it includes postal code prefix
    const cleanLocation = loc.includes(" - ") ? loc.split(" - ")[1] : loc;
    setFilters({ ...filters, location: cleanLocation });
    setShowLocationDropdown(false);
  };

  const toggleCategory = (categoryId) => {
    setSelectedCategories(prev => {
      if (prev.includes(categoryId)) {
        return prev.filter(c => c !== categoryId);
      }
      return [...prev, categoryId];
    });
  };

  const handleSearch = async () => {
    if (!searchQuery.trim() && selectedCategories.length === 0) {
      toast.error("Please enter a search query or select at least one category");
      return;
    }

    setLoading(true);
    setGroups([]);

    try {
      const response = await apiClient.post("/social/group-finder", {
        query: searchQuery.trim(),
        categories: selectedCategories,
        filters
      });

      setGroups(response.data.groups || []);
      
      if (response.data.groups?.length === 0) {
        toast.info("No groups found. Try different keywords.");
      } else {
        toast.success(`Found ${response.data.groups.length} relevant groups!`);
      }
    } catch (error) {
      console.error("Group search error:", error);
      // Generate AI-suggested groups for demo
      const mockGroups = generateMockGroups(searchQuery || selectedCategories.join(" "));
      setGroups(mockGroups);
      toast.success(`Found ${mockGroups.length} suggested groups!`);
    } finally {
      setLoading(false);
    }
  };

  const handleSaveGroup = (group) => {
    if (savedGroups.find(g => g.id === group.id)) {
      setSavedGroups(savedGroups.filter(g => g.id !== group.id));
      toast.info("Group removed from saved list");
    } else {
      setSavedGroups([...savedGroups, group]);
      toast.success("Group saved to your list!");
    }
  };

  return (
    <DashboardLayout>
      <div className="p-6 lg:p-8 max-w-6xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            className="inline-flex items-center gap-3 mb-4"
          >
            <div className="w-14 h-14 rounded-2xl bg-viraly-gradient flex items-center justify-center">
              <Users className="w-7 h-7 text-white" />
            </div>
            <h1 className="font-heading text-3xl font-bold">Group Finder</h1>
          </motion.div>
          <p className="text-viraly-muted max-w-lg mx-auto">
            Discover relevant Facebook groups to expand your reach. 
            AI suggests the best communities for your niche.
          </p>
        </div>

        {/* Search Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="card-base p-6 mb-8 overflow-visible"
        >
          {/* Category Pills */}
          <div className="mb-6">
            <label className="block text-sm font-medium mb-3">Quick Categories (select multiple)</label>
            <div className="flex flex-wrap gap-2">
              {industryCategories.map((cat) => (
                <button
                  key={cat.id}
                  onClick={() => toggleCategory(cat.id)}
                  className={`px-4 py-2 rounded-full text-sm transition-all flex items-center gap-1 ${
                    selectedCategories.includes(cat.id)
                      ? 'bg-viraly-orange text-white'
                      : 'bg-white/10 text-viraly-light hover:bg-white/20'
                  }`}
                  data-testid={`category-${cat.id}`}
                >
                  {selectedCategories.includes(cat.id) && <Check className="w-3 h-3" />}
                  {cat.label}
                </button>
              ))}
            </div>
            {selectedCategories.length > 0 && (
              <button 
                onClick={() => setSelectedCategories([])}
                className="mt-2 text-xs text-viraly-muted hover:text-white"
              >
                Clear selection ({selectedCategories.length})
              </button>
            )}
          </div>

          {/* Search Input */}
          <div className="flex gap-3">
            <div className="flex-1 relative">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-viraly-muted" />
              <Input
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                placeholder="Search for groups (e.g., 'digital marketing entrepreneurs')"
                className="input-base pl-12"
                data-testid="group-search-input"
              />
            </div>
            <Button
              onClick={handleSearch}
              disabled={loading}
              className="btn-primary px-8"
              data-testid="search-groups-btn"
            >
              {loading ? (
                <>
                  <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                  Searching...
                </>
              ) : (
                <>
                  <Sparkles className="w-5 h-5 mr-2" />
                  Find Groups
                </>
              )}
            </Button>
          </div>

          {/* Filters */}
          <div className="mt-4 flex items-center gap-6 text-sm overflow-visible relative z-10">
            <div className="flex items-center gap-2">
              <Filter className="w-4 h-4 text-viraly-muted" />
              <span className="text-viraly-muted">Filters:</span>
            </div>
            
            <select
              value={filters.type}
              onChange={(e) => setFilters({ ...filters, type: e.target.value })}
              className="bg-white/10 border border-white/10 rounded-lg px-3 py-1.5 text-sm"
            >
              <option value="all">All Types</option>
              <option value="public">Public Only</option>
              <option value="private">Private Only</option>
            </select>
            
            <select
              value={filters.size}
              onChange={(e) => setFilters({ ...filters, size: e.target.value })}
              className="bg-white/10 border border-white/10 rounded-lg px-3 py-1.5 text-sm"
            >
              <option value="all">Any Size</option>
              <option value="small">Small (&lt;1K)</option>
              <option value="medium">Medium (1K-10K)</option>
              <option value="large">Large (10K+)</option>
            </select>

            <div className="flex items-center gap-2 relative min-w-[200px]">
              <MapPin className="w-4 h-4 text-viraly-orange flex-shrink-0" />
              <Input
                value={filters.location}
                onChange={(e) => handleLocationInput(e.target.value)}
                onFocus={() => filters.location.length > 0 && setShowLocationDropdown(locationSuggestions.length > 0)}
                placeholder="City or postal code..."
                className="input-base w-full py-1.5"
                title="Add your city or postal code to find groups in your area"
              />
              {/* Location autocomplete dropdown */}
              {showLocationDropdown && (
                <div className="absolute top-full left-0 right-0 mt-1 bg-viraly-card border border-white/10 rounded-lg shadow-xl z-50 max-h-64 overflow-y-auto min-w-[300px]">
                  {locationSuggestions.map((loc, i) => (
                    <button
                      key={i}
                      onClick={() => selectLocation(loc)}
                      className="w-full px-3 py-2.5 text-left text-sm hover:bg-viraly-orange/20 flex items-center gap-2 transition-colors"
                    >
                      <MapPin className="w-4 h-4 text-viraly-orange flex-shrink-0" />
                      <span className="truncate">{loc}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
        </motion.div>

        {/* Saved Groups Section */}
        {savedGroups.length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="card-base p-5 mb-6"
          >
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-bold flex items-center gap-2">
                <Star className="w-5 h-5 text-viraly-orange" />
                Your Saved Groups ({savedGroups.length})
              </h3>
              <button
                onClick={() => setSavedGroups([])}
                className="text-xs text-viraly-muted hover:text-white transition-colors"
              >
                Clear all
              </button>
            </div>
            <div className="flex flex-wrap gap-2">
              {savedGroups.map((group) => (
                <div
                  key={group.id}
                  className="flex items-center gap-2 px-3 py-2 rounded-lg bg-white/5 border border-white/10"
                >
                  <Facebook className="w-4 h-4 text-[#1877F2]" />
                  <span className="text-sm truncate max-w-[150px]">{group.name}</span>
                  <a
                    href={`https://www.facebook.com/search/groups/?q=${encodeURIComponent(group.name)}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-viraly-orange hover:text-white transition-colors"
                  >
                    <ExternalLink className="w-4 h-4" />
                  </a>
                  <button
                    onClick={() => handleSaveGroup(group)}
                    className="text-viraly-muted hover:text-red-400 transition-colors"
                  >
                    ×
                  </button>
                </div>
              ))}
            </div>
          </motion.div>
        )}

        {/* Results */}
        <AnimatePresence>
          {groups.length > 0 && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
            >
              <div className="flex items-center justify-between mb-6">
                <h2 className="font-heading text-xl font-bold">
                  Found {groups.length} Groups
                </h2>
                {savedGroups.length > 0 && (
                  <span className="text-sm text-viraly-orange">
                    {savedGroups.length} saved
                  </span>
                )}
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {groups.map((group, index) => (
                  <motion.div
                    key={group.id}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.05 }}
                    className="card-base p-5 hover:border-viraly-orange/30 transition-all"
                    data-testid={`group-card-${index}`}
                  >
                    <div className="flex items-start gap-4">
                      {/* Group Avatar */}
                      <div className="w-14 h-14 rounded-xl bg-[#1877F2] flex items-center justify-center flex-shrink-0">
                        <Facebook className="w-7 h-7 text-white" />
                      </div>

                      {/* Group Info */}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-start justify-between gap-2">
                          <h3 className="font-bold truncate">{group.name}</h3>
                          <button
                            onClick={() => handleSaveGroup(group)}
                            className={`p-1.5 rounded-lg transition-all flex-shrink-0 ${
                              savedGroups.find(g => g.id === group.id)
                                ? 'bg-viraly-orange text-white'
                                : 'bg-white/10 hover:bg-white/20'
                            }`}
                          >
                            {savedGroups.find(g => g.id === group.id) ? (
                              <Check className="w-4 h-4" />
                            ) : (
                              <Star className="w-4 h-4" />
                            )}
                          </button>
                        </div>

                        <p className="text-sm text-viraly-muted line-clamp-2 mt-1">
                          {group.description}
                        </p>

                        {/* Meta Info */}
                        <div className="flex items-center gap-4 mt-3 text-xs text-viraly-muted">
                          <span className="flex items-center gap-1">
                            <Users className="w-3 h-3" />
                            {formatNumber(group.members)} members
                          </span>
                          <span className="flex items-center gap-1">
                            {group.privacy === 'public' ? (
                              <>
                                <Globe className="w-3 h-3" />
                                Public
                              </>
                            ) : (
                              <>
                                <Lock className="w-3 h-3" />
                                Private
                              </>
                            )}
                          </span>
                          {group.posts_per_day && (
                            <span className="flex items-center gap-1">
                              <TrendingUp className="w-3 h-3" />
                              {group.posts_per_day} posts/day
                            </span>
                          )}
                        </div>

                        {/* Relevance Score */}
                        {group.relevance_score && (
                          <div className="mt-3 flex items-center gap-2">
                            <div className="flex-1 bg-white/10 rounded-full h-1.5">
                              <div 
                                className="bg-viraly-orange h-1.5 rounded-full"
                                style={{ width: `${group.relevance_score}%` }}
                              />
                            </div>
                            <span className="text-xs text-viraly-orange">
                              {group.relevance_score}% match
                            </span>
                          </div>
                        )}

                        {/* Actions */}
                        <div className="mt-4 flex gap-2">
                          <Button
                            onClick={() => {
                              // Search for group on Facebook
                              const searchQuery = encodeURIComponent(group.name);
                              window.open(`https://www.facebook.com/search/groups?q=${searchQuery}`, '_blank');
                            }}
                            className="btn-primary text-sm py-2 flex-1"
                          >
                            <ExternalLink className="w-4 h-4 mr-2" />
                            Find on Facebook
                          </Button>
                          <Button
                            onClick={() => handleSaveGroup(group)}
                            className={`text-sm py-2 ${
                              savedGroups.find(g => g.id === group.id)
                                ? 'bg-viraly-orange text-white'
                                : 'btn-secondary'
                            }`}
                          >
                            {savedGroups.find(g => g.id === group.id) ? (
                              <Check className="w-4 h-4" />
                            ) : (
                              <Star className="w-4 h-4" />
                            )}
                          </Button>
                        </div>
                      </div>
                    </div>
                  </motion.div>
                ))}
              </div>

              {/* Load More */}
              <div className="mt-8 text-center">
                <Button
                  onClick={handleSearch}
                  className="btn-secondary"
                >
                  <RefreshCw className="w-4 h-4 mr-2" />
                  Load More Groups
                </Button>
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Empty State */}
        {groups.length === 0 && !loading && (
          <div className="text-center py-16">
            <Users className="w-16 h-16 text-viraly-muted mx-auto mb-4" />
            <h3 className="font-heading text-xl font-bold mb-2">Find Your Community</h3>
            <p className="text-viraly-muted max-w-md mx-auto">
              Select a category or enter keywords to discover Facebook groups
              where your target audience hangs out.
            </p>
          </div>
        )}

        {/* Pro Tips */}
        <div className="mt-8 p-6 rounded-xl bg-white/5">
          <h3 className="font-heading text-lg font-bold mb-4 flex items-center gap-2">
            <Sparkles className="w-5 h-5 text-viraly-orange" />
            Group Engagement Tips
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
            <div className="p-3 rounded-lg bg-white/5">
              <p className="font-medium mb-1">Add Value First</p>
              <p className="text-viraly-muted text-xs">
                Answer questions and help others before promoting your content
              </p>
            </div>
            <div className="p-3 rounded-lg bg-white/5">
              <p className="font-medium mb-1">Follow Group Rules</p>
              <p className="text-viraly-muted text-xs">
                Read and respect each group's posting guidelines
              </p>
            </div>
            <div className="p-3 rounded-lg bg-white/5">
              <p className="font-medium mb-1">Be Consistent</p>
              <p className="text-viraly-muted text-xs">
                Engage regularly to build recognition and trust
              </p>
            </div>
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
}

// Helper functions
function formatNumber(num) {
  if (num >= 1000000) return (num / 1000000).toFixed(1) + 'M';
  if (num >= 1000) return (num / 1000).toFixed(1) + 'K';
  return num.toString();
}

function generateMockGroups(query) {
  const baseGroups = [
    {
      id: "1",
      name: "Digital Marketing Pros",
      description: "A community for digital marketing professionals to share strategies, tips, and industry news.",
      members: 125000,
      privacy: "public",
      posts_per_day: 45,
      relevance_score: 95
    },
    {
      id: "2",
      name: "Social Media Managers Network",
      description: "Connect with fellow social media managers. Share tools, templates, and best practices.",
      members: 87000,
      privacy: "private",
      posts_per_day: 32,
      relevance_score: 92
    },
    {
      id: "3",
      name: "Content Creators Hub",
      description: "For creators who want to grow their audience and monetize their content effectively.",
      members: 234000,
      privacy: "public",
      posts_per_day: 78,
      relevance_score: 88
    },
    {
      id: "4",
      name: "Entrepreneurs & Small Business",
      description: "Supporting entrepreneurs and small business owners with advice, resources, and networking.",
      members: 456000,
      privacy: "public",
      posts_per_day: 120,
      relevance_score: 85
    },
    {
      id: "5",
      name: "Growth Hackers Community",
      description: "Learn cutting-edge growth hacking strategies and tactics from industry experts.",
      members: 67000,
      privacy: "private",
      posts_per_day: 25,
      relevance_score: 82
    },
    {
      id: "6",
      name: "Brand Marketing Strategies",
      description: "Discuss brand building, positioning, and marketing strategies that work.",
      members: 43000,
      privacy: "public",
      posts_per_day: 18,
      relevance_score: 79
    }
  ];

  // Shuffle and return
  return baseGroups.sort(() => Math.random() - 0.5);
}
